import { ConfigProvider, DatePicker, Form, Input, Select } from "antd";
import {
  MdAccessTime,
  MdAspectRatio,
  MdEvent,
  MdLocalOffer,
  MdMovieFilter,
  MdPublic,
  MdSubject,
  MdVideoCameraBack,
} from "react-icons/md";
import { useAtomValue } from "jotai";
import moment from "moment";
import { masterAtom } from "../../../../atoms/masters.atom";
import { masterToOptions } from "../../../../utils/functions/misc/masterToOptions";
import { countries } from "../../../../data/countries";
import { StatePair } from "../../../../types/SetState";
import { TextAreaResize } from "../../../../components/ui/textarea-resize/textarea-resize.component";
import {
  DurationRow,
  Field,
  FieldGrid,
  HelperRow,
  Section,
} from "../../components/section-card.component";

const SYNOPSIS_LIMIT = 1500;

export const StepTwo = ({
  keywords,
  setKeywords,
  synopsis,
  setSynopsis,
  visible = true,
  fillHeight,
}: StatePair<"keywords", "setKeywords", string[]> &
  StatePair<"synopsis", "setSynopsis", string> & {
    visible?: boolean;
    fillHeight?: boolean;
  }) => {
  const synopsisCount = synopsis?.length ?? 0;
  const master = useAtomValue(masterAtom);

  return (
    <div
      style={{
        display: visible ? "flex" : "none",
        width: "100%",
        height: "100%",
        minHeight: 0,
        flexDirection: "column",
      }}
    >
      <Section
        icon={<MdMovieFilter />}
        title="Technical Details"
        subtitle="How was the film shot, when was it completed, and where was it made?"
        fillHeight={fillHeight}
      >
        <FieldGrid $cols={3}>
          <Field
            label="Duration"
            icon={<MdAccessTime />}
            required
            helper="Total runtime in minutes : seconds."
          >
            <DurationRow>
              <Form.Item
                noStyle
                name={["duration", "mm"]}
                rules={[
                  { required: true, message: "" },
                  {
                    validator: (_, value) => {
                      const newValue = String(value);
                      if (newValue.startsWith("0") && newValue.length > 2) {
                        return Promise.reject("No leading zeroes!");
                      }
                      return Promise.resolve();
                    },
                    message: "No leading zeroes!",
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder="mm"
                  min="0"
                  size="large"
                />
              </Form.Item>
              <span className="duration-separator">:</span>
              <Form.Item
                noStyle
                name={["duration", "ss"]}
                rules={[
                  { required: true, message: "" },
                  {
                    validator: (_, value) => {
                      const newValue = String(value);
                      if (newValue.startsWith("0") && newValue.length > 2) {
                        return Promise.reject("No leading zeroes!");
                      }
                      return Promise.resolve();
                    },
                    message: "No leading zeroes!",
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder="ss"
                  min="0"
                  max={60}
                  size="large"
                />
              </Form.Item>
            </DurationRow>
          </Field>
          <Field
            name="date_of_completion"
            label="Date of Completion"
            icon={<MdEvent />}
            required
            rules={[{ required: true }]}
            helper="When did principal photography wrap?"
          >
            <DatePicker
              size="large"
              format="DD-MM-YYYY"
              placeholder="Select date"
              style={{ width: "100%" }}
              disabledDate={(date) => date && +date > moment.now()}
            />
          </Field>
          <Field
            name="country"
            label="Country of Production"
            icon={<MdPublic />}
            required
            rules={[{ required: true }]}
            helper="Where was the film primarily produced?"
          >
            <Select
              size="large"
              options={countries}
              placeholder="Select country"
              showSearch
              allowClear
            />
          </Field>
          <Field
            name="shooting_format"
            label="Shooting Format"
            icon={<MdVideoCameraBack />}
            required
            rules={[{ required: true }]}
            helper="The capture format used during production."
          >
            <Select
              size="large"
              showSearch
              options={masterToOptions("shooting_format", master)}
              placeholder="Select format"
            />
          </Field>
          <Field
            name="aspect_ratio"
            label="Aspect Ratio"
            icon={<MdAspectRatio />}
            required
            rules={[{ required: true }]}
            helper="The original aspect ratio your film was framed in."
          >
            <Select
              size="large"
              showSearch
              options={masterToOptions("aspect_ratio", master)}
              placeholder="Select ratio"
            />
          </Field>
          <Field
            name="keywords"
            label="Keywords"
            icon={<MdLocalOffer />}
            helper="Type and press Enter — these help your film get discovered."
          >
            <ConfigProvider renderEmpty={() => "Type to add a keyword…"}>
              <Select
                size="large"
                showSearch
                value={keywords}
                onChange={setKeywords}
                mode="tags"
                placeholder="Add keywords"
              />
            </ConfigProvider>
          </Field>
          <Field
            name="synopsis"
            label="Synopsis"
            icon={<MdSubject />}
            required
            helper={
              <HelperRow>
                <span>
                  Audiences see this on the listing page. Hook them in a few
                  sentences.
                </span>
                <span>
                  {synopsisCount} / {SYNOPSIS_LIMIT}
                </span>
              </HelperRow>
            }
            fullWidth
          >
            <TextAreaResize
              placeholder="What is your film about?"
              value={synopsis}
              onChange={setSynopsis}
              rows={4}
              maxLength={SYNOPSIS_LIMIT}
            />
          </Field>
        </FieldGrid>
      </Section>
    </div>
  );
};
